/**
 * 图纸管理 - 图纸库页面（产品为中心）
 * Tab1 产品：卡片式产品列表 + 两模式创建向导（上传已有产品 / 新增产品）+ 四要素详情抽屉
 * Tab2 图纸检索：全部图纸平铺检索（素材级 + 产品级）
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Search, Trash2, Loader2, X, FileText, Image as ImageIcon, Download,
  RefreshCw, Upload, CheckCircle2, AlertTriangle, Layers, Package, FileSpreadsheet,
  Eye, Link2, Pencil,
} from 'lucide-react';
import DrawingUploader from '../components/DrawingUploader';
import SearchableSelect from '../components/SearchableSelect';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import type {
  DrawingComponentType, DrawingMaterialListItem, DrawingProductListItem,
  DrawingProduct, DrawingFileItem, DrawingDoc, DrawingPreviewBlock,
} from '../types/api';
import styles from './DrawingLibrary.module.css';

const DRAWING_EXTS = ['pdf', 'dwg', 'dxf', 'step', 'stp', 'igs', 'jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff', 'zip', 'rar', 'doc', 'docx', 'xls', 'xlsx'];
const PREVIEWABLE = ['pdf', 'jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff'];
const formatSize = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : b < 1073741824 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1073741824).toFixed(2)} GB`);

export default function DrawingLibraryPage() {
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const { hasPermission } = useAuth();
  const canManageTemplate = hasPermission('drawing:模板配置');

  const [activeTab, setActiveTab] = useState<'products' | 'files'>('products');

  // ==================== 产品列表 ====================
  const [products, setProducts] = useState<DrawingProductListItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productSearch, setProductSearch] = useState('');

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await api.getDrawingProducts({ keyword: productSearch || undefined, page: 1, page_size: 100 });
      setProducts(res.data || []);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setProductsLoading(false); }
  }, [productSearch, showToast]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // ==================== 创建向导 ====================
  const [showWizard, setShowWizard] = useState(false);
  const [wizardMode, setWizardMode] = useState<'upload' | 'new' | null>(null); // upload=上传已有产品 new=新增产品
  const [wizardStep, setWizardStep] = useState(1); // 1 基本信息+选素材 → 2 图纸 → 3 文档 → 完成
  const [wizardTypes, setWizardTypes] = useState<DrawingComponentType[]>([]);
  const [typeMaterials, setTypeMaterials] = useState<Record<number, DrawingMaterialListItem[]>>({});
  const [wizardForm, setWizardForm] = useState({ product_name: '', remark: '' });
  const [wizardSelection, setWizardSelection] = useState<Record<number, number>>({}); // type_id -> material_id
  const [wizardProductId, setWizardProductId] = useState<number | null>(null);
  const [wizardDrawings, setWizardDrawings] = useState<DrawingFileItem[]>([]);
  const [docUploads, setDocUploads] = useState<{ param_card?: File; manual?: File }>({});
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [wizardBusy, setWizardBusy] = useState(false);

  const openWizard = async () => {
    setWizardMode(null); setWizardStep(1);
    setWizardForm({ product_name: '', remark: '' });
    setWizardSelection({}); setWizardProductId(null); setWizardDrawings([]);
    setDocUploads({}); setAutoGenerate(true);
    setShowWizard(true);
    try {
      const res = await api.getDrawingComponentTypes();
      const enabled = (res.data || []).filter(t => t.is_enabled);
      setWizardTypes(enabled);
      // 并行加载各类型素材
      const results = await Promise.all(enabled.map(t => api.getDrawingMaterials({ component_type_id: t.component_type_id, page_size: 100 })));
      const map: Record<number, DrawingMaterialListItem[]> = {};
      enabled.forEach((t, i) => { map[t.component_type_id] = results[i].data || []; });
      setTypeMaterials(map);
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  // 素材按需选择：至少一个部件类型选了素材即可（未选类型不参与组合）
  const selectionComplete = wizardTypes.length > 0 && wizardTypes.some(t => wizardSelection[t.component_type_id]);

  const handleCreateProduct = async () => {
    if (!wizardForm.product_name.trim()) { showToast('warning', '请输入产品名称'); return; }
    if (!selectionComplete) { showToast('warning', '请至少为一个部件类型选择素材'); return; }
    setWizardBusy(true);
    try {
      const items = Object.entries(wizardSelection).map(([tid, mid]) => ({ component_type_id: Number(tid), material_id: mid }));
      const res = await api.createDrawingProduct({
        product_name: wizardForm.product_name,
        remark: wizardForm.remark || undefined,
        items,
      });
      setWizardProductId(res.data!.product_id);
      if (wizardMode === 'new') {
        // 新增产品：立即自动生成两份文档
        await api.generateDrawingDocuments(res.data!.product_id, ['param_card', 'manual']);
        showToast('success', '产品已创建，参数卡与说明书已生成（产品图纸可后补）');
        setShowWizard(false); loadProducts();
      } else {
        setWizardStep(2); // 上传已有产品：继续传图纸
      }
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setWizardBusy(false); }
  };

  const handleWizardFinish = async () => {
    if (!wizardProductId) return;
    setWizardBusy(true);
    try {
      // 上传的文档优先；缺失且勾选自动生成时补齐
      const genTypes: Array<'param_card' | 'manual'> = [];
      for (const dt of ['param_card', 'manual'] as const) {
        const f = docUploads[dt];
        if (f) {
          await api.uploadDrawingDocument(wizardProductId, dt, f);
        } else if (autoGenerate) {
          genTypes.push(dt);
        }
      }
      if (genTypes.length > 0) {
        await api.generateDrawingDocuments(wizardProductId, genTypes);
      }
      showToast('success', '产品创建完成');
      setShowWizard(false); loadProducts();
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setWizardBusy(false); }
  };

  // ==================== 产品详情抽屉 ====================
  const [detailProduct, setDetailProduct] = useState<DrawingProduct | null>(null);
  const [detailDocs, setDetailDocs] = useState<DrawingDoc[]>([]);
  const [detailDrawings, setDetailDrawings] = useState<DrawingFileItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [detailSelection, setDetailSelection] = useState<Record<number, number>>({});
  const [uploadDocType, setUploadDocType] = useState<'param_card' | 'manual' | null>(null);

  const openDetail = async (p: DrawingProductListItem) => {
    setDetailLoading(true);
    setDetailProduct(null);
    setEditingItems(false);
    try {
      const [prodRes, docsRes] = await Promise.all([
        api.getDrawingProduct(p.product_id),
        api.getDrawingProductDocuments(p.product_id),
      ]);
      setDetailProduct(prodRes.data!);
      setDetailDocs(docsRes.data || []);
      const drRes = await api.getDrawingFiles({ scope: 'product', product_id: p.product_id, page_size: 100 });
      setDetailDrawings(drRes.data || []);
      setDetailSelection(Object.fromEntries(prodRes.data!.items.map(i => [i.component_type_id, i.material_id])));
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setDetailLoading(false); }
  };

  const refreshDetail = async (pid: number) => {
    const [prodRes, docsRes, drRes] = await Promise.all([
      api.getDrawingProduct(pid),
      api.getDrawingProductDocuments(pid),
      api.getDrawingFiles({ scope: 'product', product_id: pid, page_size: 100 }),
    ]);
    setDetailProduct(prodRes.data!);
    setDetailDocs(docsRes.data || []);
    setDetailDrawings(drRes.data || []);
  };

  const handleSaveItems = async () => {
    if (!detailProduct) return;
    if (wizardTypes.length > 0 && !wizardTypes.some(t => detailSelection[t.component_type_id])) {
      showToast('warning', '请至少为一个部件类型选择素材'); return;
    }
    try {
      const items = Object.entries(detailSelection).map(([tid, mid]) => ({ component_type_id: Number(tid), material_id: mid }));
      await api.updateDrawingProduct(detailProduct.product_id, { items });
      showToast('success', '素材组合已更新（原文档将标记为可能过期）');
      setEditingItems(false);
      await refreshDetail(detailProduct.product_id);
      loadProducts();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleRegenerate = async (docType: 'param_card' | 'manual') => {
    if (!detailProduct) return;
    try {
      const current = detailDocs.find(d => d.doc_type === docType && d.is_current);
      if (current) {
        await api.regenerateDrawingDocument(current.doc_id);
      } else {
        await api.generateDrawingDocuments(detailProduct.product_id, [docType]);
      }
      showToast('success', '已重新生成');
      await refreshDetail(detailProduct.product_id); loadProducts();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleUploadDoc = async (docType: 'param_card' | 'manual', file: File) => {
    if (!detailProduct) return;
    try {
      await api.uploadDrawingDocument(detailProduct.product_id, docType, file);
      showToast('success', '文档已上传（成为当前版本）');
      setUploadDocType(null);
      await refreshDetail(detailProduct.product_id); loadProducts();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleDeleteProduct = async (p: DrawingProductListItem) => {
    const ok = await confirmDialog.confirm({
      title: '确认删除', message: `确定删除产品「${p.product_name}」？其文档与图纸将保留在系统中可追溯。`,
      confirmText: '删除', danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteDrawingProduct(p.product_id);
      showToast('success', '已删除');
      if (detailProduct?.product_id === p.product_id) setDetailProduct(null);
      loadProducts();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  // ==================== 图纸检索 Tab ====================
  const [files, setFiles] = useState<DrawingFileItem[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesTotal, setFilesTotal] = useState(0);
  const [fileFilters, setFileFilters] = useState({ keyword: '', scope: '', file_ext: '' });

  const loadFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const res = await api.getDrawingFiles({
        keyword: fileFilters.keyword || undefined,
        scope: fileFilters.scope || undefined,
        file_ext: fileFilters.file_ext || undefined,
        page: 1, page_size: 100,
      });
      setFiles(res.data || []); setFilesTotal(res.total || 0);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setFilesLoading(false); }
  }, [fileFilters, showToast]);

  useEffect(() => { if (activeTab === 'files') loadFiles(); }, [activeTab, loadFiles]);

  const handleDeleteFile = async (f: DrawingFileItem) => {
    const ok = await confirmDialog.confirm({ title: '确认删除', message: `确定删除图纸「${f.file_name}」？`, confirmText: '删除', danger: true });
    if (!ok) return;
    try {
      await api.deleteDrawingFile(f.drawing_id);
      showToast('success', '已删除'); loadFiles();
      if (detailProduct) refreshDetail(detailProduct.product_id).catch(() => {});
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const currentDoc = (t: 'param_card' | 'manual') => detailDocs.find(d => d.doc_type === t && d.is_current);
  const historyDocs = (t: 'param_card' | 'manual') => detailDocs.filter(d => d.doc_type === t && !d.is_current);

  return (
    <div className={styles.container}>
      {/* ===== 页头 ===== */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>图纸库</h1>
          <p className={styles.subtitle}>产品组合、产品图纸与参数卡/说明书的统一管理</p>
        </div>
        {activeTab === 'products' && (
          <button className={styles.btnPrimary} onClick={openWizard}>
            <Plus size={15} /> 新增产品
          </button>
        )}
      </div>

      {/* ===== Tab 切换 ===== */}
      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeTab === 'products' ? styles.tabActive : ''}`} onClick={() => setActiveTab('products')}>
          <Package size={15} /> 产品
        </button>
        <button className={`${styles.tab} ${activeTab === 'files' ? styles.tabActive : ''}`} onClick={() => setActiveTab('files')}>
          <Layers size={15} /> 图纸检索
        </button>
      </div>

      {/* ==================== Tab1：产品卡片 ==================== */}
      {activeTab === 'products' && (
        <>
          <div className={styles.toolbar}>
            <div className={styles.searchBox}>
              <Search size={16} />
              <input placeholder="搜索产品名称/编码..." value={productSearch} onChange={e => setProductSearch(e.target.value)} />
            </div>
            <span className={styles.countText}>共 {products.length} 个产品</span>
          </div>

          {productsLoading ? (
            <div className={styles.loading}><Loader2 size={24} className={styles.spin} /></div>
          ) : products.length === 0 ? (
            <div className={styles.empty}>
              <Package size={36} />
              <p>暂无产品</p>
              <button className={styles.btnPrimary} onClick={openWizard}><Plus size={14} /> 创建第一个产品</button>
            </div>
          ) : (
            <div className={styles.cardGrid}>
              {products.map(p => (
                <div key={p.product_id} className={styles.card} onClick={() => openDetail(p)}>
                  <div className={styles.cardHeader}>
                    <span className={styles.cardName}>{p.product_name}</span>
                    <span className={styles.cardCode}>{p.product_code}</span>
                  </div>
                  <div className={styles.cardSummary} title={p.material_summary}>{p.material_summary || '未配置素材'}</div>
                  <div className={styles.cardMeta}>
                    <span className={p.drawing_count > 0 ? styles.badgeOk : styles.badgeWarn}>
                      {p.drawing_count > 0 ? `图纸 ${p.drawing_count}` : '缺产品图纸'}
                    </span>
                    <span className={p.has_param_card ? styles.badgeOk : styles.badgeWarn}>参数卡{p.has_param_card ? ' ✓' : ' 缺'}</span>
                    <span className={p.has_manual ? styles.badgeOk : styles.badgeWarn}>说明书{p.has_manual ? ' ✓' : ' 缺'}</span>
                  </div>
                  {p.docs_stale && (
                    <div className={styles.staleBar}><AlertTriangle size={12} /> 素材已变更，文档可能过期</div>
                  )}
                  <div className={styles.cardFooter}>
                    <span className={styles.cardTime}>{p.update_time ? new Date(p.update_time).toLocaleDateString('zh-CN') : ''}</span>
                    <div className={styles.cardActions} onClick={e => e.stopPropagation()}>
                      <button className={styles.iconBtn} title="删除" onClick={() => handleDeleteProduct(p)}><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ==================== Tab2：图纸检索 ==================== */}
      {activeTab === 'files' && (
        <>
          <div className={styles.toolbar}>
            <div className={styles.searchBox}>
              <Search size={16} />
              <input placeholder="搜索图号/文件名..." value={fileFilters.keyword} onChange={e => setFileFilters(f => ({ ...f, keyword: e.target.value }))} />
            </div>
            <div className={styles.filterGroup}>
              <select className={styles.filterSelect} value={fileFilters.scope} onChange={e => setFileFilters(f => ({ ...f, scope: e.target.value }))}>
                <option value="">全部归属</option>
                <option value="product">产品图纸</option>
                <option value="material">素材图纸</option>
              </select>
              <select className={styles.filterSelect} value={fileFilters.file_ext} onChange={e => setFileFilters(f => ({ ...f, file_ext: e.target.value }))}>
                <option value="">全部格式</option>
                {['pdf', 'dwg', 'dxf', 'step', 'jpg', 'png', 'zip'].map(x => <option key={x} value={x}>{x.toUpperCase()}</option>)}
              </select>
            </div>
            <span className={styles.countText}>共 {filesTotal} 份</span>
          </div>
          <div className={styles.tableWrap}>
            {filesLoading ? (
              <div className={styles.loading}><Loader2 size={22} className={styles.spin} /></div>
            ) : files.length === 0 ? (
              <div className={styles.empty}><Layers size={36} /><p>暂无图纸</p></div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>文件名</th>
                    <th style={{ width: '10%' }}>格式</th>
                    <th style={{ width: '10%' }}>大小</th>
                    <th style={{ width: '22%' }}>归属</th>
                    <th style={{ width: '10%' }}>上传时间</th>
                    <th style={{ width: '8%' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map(f => (
                    <tr key={f.drawing_id}>
                      <td className={styles.cellStrong}>{f.file_name}</td>
                      <td><span className={styles.extTag}>{f.file_ext.toUpperCase()}</span></td>
                      <td>{formatSize(f.file_size)}</td>
                      <td className={styles.cellEllipsis}>
                        {f.scope === 'product'
                          ? <span className={styles.scopeProduct}>产品：{f.product_name || '-'}</span>
                          : <span className={styles.scopeMaterial}>素材：{f.material_name || '-'}</span>}
                      </td>
                      <td>{f.create_time ? new Date(f.create_time).toLocaleDateString('zh-CN') : '-'}</td>
                      <td>
                        <div className={styles.rowActions}>
                          {f.url && PREVIEWABLE.includes(f.file_ext) && (
                            <button className={styles.iconBtn} title="预览" onClick={() => window.open(f.url!, '_blank')}><Eye size={14} /></button>
                          )}
                          {f.url && (
                            <button className={styles.iconBtn} title={PREVIEWABLE.includes(f.file_ext) ? '下载' : '下载（该格式不支持在线预览）'} onClick={() => window.open(f.url!, '_blank')}><Download size={14} /></button>
                          )}
                          <button className={styles.iconBtn} title="删除" onClick={() => handleDeleteFile(f)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ==================== 弹窗：创建向导 ==================== */}
      {showWizard && (
        <div className={styles.modalOverlay} onClick={() => setShowWizard(false)}>
          <div className={`${styles.modal} ${styles.modalLarge}`} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                新增产品
                {wizardMode === null && <span className={styles.hint}> — 请选择创建方式</span>}
                {wizardMode === 'upload' && <span className={styles.hint}> — 上传已有产品</span>}
                {wizardMode === 'new' && <span className={styles.hint}> — 新增产品（自动生成文档）</span>}
              </h2>
              <button className={styles.modalClose} onClick={() => setShowWizard(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              {/* 步骤0：选模式 */}
              {wizardMode === null && (
                <div className={styles.modeGrid}>
                  <div className={styles.modeCard} onClick={() => setWizardMode('upload')}>
                    <Upload size={26} />
                    <div className={styles.modeTitle}>上传已有产品</div>
                    <div className={styles.modeDesc}>已设计完成的产品：上传产品图纸（必填）→ 选择素材 → 参数卡/说明书可选上传，未上传的自动生成</div>
                  </div>
                  <div className={styles.modeCard} onClick={() => setWizardMode('new')}>
                    <FileText size={26} />
                    <div className={styles.modeTitle}>新增产品</div>
                    <div className={styles.modeDesc}>新设计的产品：选择素材组合 → 系统自动生成参数卡与说明书（图纸可后补）</div>
                  </div>
                </div>
              )}

              {wizardMode && (
                <>
                  {/* 步骤指示 */}
                  <div className={styles.steps}>
                    <span className={`${styles.stepDot} ${wizardStep >= 1 ? styles.stepActive : ''}`}>1 基本信息与素材</span>
                    {wizardMode === 'upload' && (
                      <span className={`${styles.stepDot} ${wizardStep >= 2 ? styles.stepActive : ''}`}>2 产品图纸</span>
                    )}
                    <span className={`${styles.stepDot} ${wizardMode === 'new' ? styles.stepActive : ''}`}>{wizardMode === 'upload' ? 3 : 2} 文档</span>
                  </div>

                  {/* 步骤1：基本信息 + 选素材 */}
                  {wizardStep === 1 && (
                    <>
                      <div className={styles.formGrid}>
                        <div className={styles.formField}>
                          <label>产品名称 <span className={styles.required}>*</span></label>
                          <input value={wizardForm.product_name} onChange={e => setWizardForm(p => ({ ...p, product_name: e.target.value }))} placeholder="如：900g 智能包装奶粉罐" />
                        </div>
                        <div className={styles.formField}>
                          <label>备注</label>
                          <input value={wizardForm.remark} onChange={e => setWizardForm(p => ({ ...p, remark: e.target.value }))} />
                        </div>
                      </div>
                      <div className={styles.sectionTitle}>选择素材（按需选择，可留空）</div>
                      {wizardTypes.length === 0 ? (
                        <div className={styles.noFields}>暂无启用的部件类型，请先到素材库配置</div>
                      ) : (
                        <div className={styles.selectorList}>
                          {wizardTypes.map(t => (
                            <div key={t.component_type_id} className={styles.selectorRow}>
                              <span className={styles.selectorLabel}>{t.type_name}</span>
                              <SearchableSelect
                                value={wizardSelection[t.component_type_id] ?? null}
                                options={(typeMaterials[t.component_type_id] || []).map(m => ({ value: m.material_id, label: `${m.material_name}（${m.material_code}）` }))}
                                onChange={v => setWizardSelection(prev => ({ ...prev, [t.component_type_id]: v }))}
                                onClear={() => setWizardSelection(prev => { const next = { ...prev }; delete next[t.component_type_id]; return next; })}
                                placeholder="请选择素材"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className={styles.wizardFooter}>
                        <button className={styles.btnPrimary} disabled={wizardBusy || !selectionComplete || wizardTypes.length === 0} onClick={handleCreateProduct}>
                          {wizardBusy ? <Loader2 size={14} className={styles.spin} /> : '创建产品并继续'}
                        </button>
                      </div>
                    </>
                  )}

                  {/* 步骤2：上传产品图纸（模式A必填） */}
                  {wizardStep === 2 && wizardMode === 'upload' && (
                    <>
                      <p className={styles.hintBar}>产品已创建（{wizardForm.product_name}）。请上传产品图纸（必填，可多张；支持 {DRAWING_EXTS.slice(0, 8).join('/')} 等格式，单文件 ≤500MB）。</p>
                      <div className={styles.wizardUploaderRow}>
                        <DrawingUploader
                          scope="product"
                          productId={wizardProductId!}
                          onUploaded={d => setWizardDrawings(prev => [...prev, d])}
                        />
                      </div>
                      {wizardDrawings.length > 0 && (
                        <div className={styles.uploadedFiles}>
                          {wizardDrawings.map(d => (
                            <div key={d.drawing_id} className={styles.uploadedFile}><CheckCircle2 size={13} /> {d.file_name}</div>
                          ))}
                        </div>
                      )}
                      <div className={styles.wizardFooter}>
                        <button className={styles.btnPrimary} disabled={wizardDrawings.length === 0} onClick={() => setWizardStep(3)}>
                          下一步：处理文档
                        </button>
                      </div>
                    </>
                  )}

                  {/* 步骤3：文档（模式A）/ 步骤2（模式B在创建时已生成，仅模式A进入此步） */}
                  {wizardStep === 3 && wizardMode === 'upload' && (
                    <>
                      <p className={styles.hintBar}>参数卡与说明书：可选择上传已有文件（docx/pdf/图片），未上传的将自动生成。</p>
                      {(['param_card', 'manual'] as const).map(dt => (
                        <div key={dt} className={styles.docUploadRow}>
                          <span className={styles.docUploadLabel}>{dt === 'param_card' ? '参数卡' : '说明书'}</span>
                          <label className={styles.filePick}>
                            <Upload size={13} /> {docUploads[dt] ? docUploads[dt]!.name : '选择文件上传'}
                            <input type="file" accept=".docx,.pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                              onChange={e => { const f = e.target.files?.[0]; if (f) setDocUploads(prev => ({ ...prev, [dt]: f })); }} />
                          </label>
                          {docUploads[dt] && (
                            <button className={styles.iconBtn} title="移除" onClick={() => setDocUploads(prev => ({ ...prev, [dt]: undefined }))}><X size={14} /></button>
                          )}
                        </div>
                      ))}
                      <label className={styles.checkRow}>
                        <input type="checkbox" checked={autoGenerate} onChange={e => setAutoGenerate(e.target.checked)} />
                        未上传的文档由系统按当前模板自动生成
                      </label>
                      <div className={styles.wizardFooter}>
                        <button className={styles.btnPrimary} disabled={wizardBusy} onClick={handleWizardFinish}>
                          {wizardBusy ? <Loader2 size={14} className={styles.spin} /> : '完成创建'}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== 抽屉：产品详情（四要素） ==================== */}
      {(detailProduct || detailLoading) && (
        <div className={styles.drawerOverlay} onClick={() => setDetailProduct(null)}>
          <div className={styles.drawer} onClick={e => e.stopPropagation()}>
            {detailLoading || !detailProduct ? (
              <div className={styles.loading}><Loader2 size={24} className={styles.spin} /></div>
            ) : (
              <>
                <div className={styles.drawerHeader}>
                  <div>
                    <h2>{detailProduct.product_name}</h2>
                    <span className={styles.drawerSub}>{detailProduct.product_code}
                      {detailProduct.docs_stale && <span className={styles.staleTag}><AlertTriangle size={11} /> 文档可能过期</span>}
                    </span>
                  </div>
                  <button className={styles.modalClose} onClick={() => setDetailProduct(null)}><X size={18} /></button>
                </div>
                <div className={styles.drawerBody}>
                  {/* 要素1：产品图纸 */}
                  <div className={styles.element}>
                    <div className={styles.elementTitle}>产品图纸（{detailDrawings.length}）</div>
                    {detailDrawings.length === 0 && <div className={styles.noFields}>暂无产品图纸（卡片上会显示“缺”角标）</div>}
                    {detailDrawings.map(d => (
                      <div key={d.drawing_id} className={styles.fileRow}>
                        <FileText size={14} />
                        <span className={styles.fileName}>{d.file_name}</span>
                        <span className={styles.fileMeta}>{formatSize(d.file_size)}</span>
                        <div className={styles.rowActions}>
                          {d.url && PREVIEWABLE.includes(d.file_ext) && (
                            <button className={styles.iconBtn} title="预览" onClick={() => window.open(d.url!, '_blank')}><Eye size={13} /></button>
                          )}
                          {d.url && <button className={styles.iconBtn} title="下载" onClick={() => window.open(d.url!, '_blank')}><Download size={13} /></button>}
                          <button className={styles.iconBtn} title="删除" onClick={() => handleDeleteFile(d)}><Trash2 size={13} /></button>
                        </div>
                      </div>
                    ))}
                    <DrawingUploader
                      scope="product"
                      productId={detailProduct.product_id}
                      onUploaded={d => { setDetailDrawings(prev => [d, ...prev]); loadProducts(); }}
                    />
                  </div>

                  {/* 要素2：素材组合 */}
                  <div className={styles.element}>
                    <div className={styles.elementTitleRow}>
                      <span className={styles.elementTitle}>素材组合（{detailProduct.items.length}）</span>
                      {!editingItems ? (
                        <button className={styles.btnSmall} onClick={() => { setEditingItems(true); wizardTypes.length === 0 && api.getDrawingComponentTypes().then(r => setWizardTypes((r.data || []).filter(t => t.is_enabled))); }}>
                          <Pencil size={12} /> 编辑
                        </button>
                      ) : (
                        <div className={styles.rowActions}>
                          <button className={styles.btnSmall} onClick={() => { setEditingItems(false); setDetailSelection(Object.fromEntries(detailProduct.items.map(i => [i.component_type_id, i.material_id]))); }}>取消</button>
                          <button className={styles.btnSmallPrimary} onClick={handleSaveItems}>保存</button>
                        </div>
                      )}
                    </div>
                    {!editingItems ? (
                      <div className={styles.itemList}>
                        {detailProduct.items.map(i => (
                          <div key={i.component_type_id} className={styles.itemRow}>
                            <span className={styles.itemType}>{i.component_type_name}</span>
                            <span className={styles.itemMaterial}>{i.material_name}（{i.material_code}）</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.selectorList}>
                        {wizardTypes.map(t => (
                          <div key={t.component_type_id} className={styles.selectorRow}>
                            <span className={styles.selectorLabel}>{t.type_name}</span>
                            <SearchableSelect
                              value={detailSelection[t.component_type_id] ?? null}
                              options={(typeMaterials[t.component_type_id] || []).map(m => ({ value: m.material_id, label: `${m.material_name}（${m.material_code}）` }))}
                              onChange={v => setDetailSelection(prev => ({ ...prev, [t.component_type_id]: v }))}
                              onClear={() => setDetailSelection(prev => { const next = { ...prev }; delete next[t.component_type_id]; return next; })}
                              placeholder="请选择素材"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 要素3/4：参数卡与说明书 */}
                  {(['param_card', 'manual'] as const).map(dt => {
                    const cur = currentDoc(dt);
                    return (
                      <div key={dt} className={styles.element}>
                        <div className={styles.elementTitleRow}>
                          <span className={styles.elementTitle}>{dt === 'param_card' ? '参数卡' : '说明书'}</span>
                          <div className={styles.rowActions}>
                            <button className={styles.btnSmall} onClick={() => handleRegenerate(dt)}><RefreshCw size={12} /> 生成/重新生成</button>
                            <button className={`${styles.btnSmall} ${uploadDocType === dt ? styles.btnSmallActive : ''}`} onClick={() => setUploadDocType(uploadDocType === dt ? null : dt)}><Upload size={12} /> 上传替换</button>
                          </div>
                        </div>
                        {uploadDocType === dt && (
                          <label className={styles.filePick} style={{ marginBottom: 8 }}>
                            <Upload size={13} /> 选择文件（docx/pdf/图片）
                            <input type="file" accept=".docx,.pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                              onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadDoc(dt, f); }} />
                          </label>
                        )}
                        {cur ? (
                          <div className={styles.fileRow}>
                            <FileSpreadsheet size={14} />
                            <span className={styles.fileName}>{cur.file_name}</span>
                            <span className={`${styles.sourceTag} ${cur.source === 'generated' ? styles.sourceGen : styles.sourceUpl}`}>
                              {cur.source === 'generated' ? '生成' : '上传'} v{cur.version}
                            </span>
                            <div className={styles.rowActions}>
                              <button className={styles.iconBtn} title="下载" onClick={() => api.downloadDrawingDocument(cur.doc_id)}><Download size={13} /></button>
                            </div>
                          </div>
                        ) : <div className={styles.noFields}>尚未生成</div>}
                        {historyDocs(dt).length > 0 && (
                          <div className={styles.historyList}>
                            <div className={styles.historyTitle}>历史版本</div>
                            {historyDocs(dt).map(h => (
                              <div key={h.doc_id} className={styles.historyRow}>
                                <span>v{h.version} · {h.source === 'generated' ? '生成' : '上传'} · {h.create_time ? new Date(h.create_time).toLocaleString('zh-CN') : ''}</span>
                                <button className={styles.iconBtn} title="下载" onClick={() => api.downloadDrawingDocument(h.doc_id)}><Download size={12} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
